import React from "react";
import { NavLink, Route, Switch } from "react-router-dom";
import * as CHQ from "chq";

import { useProfile } from "app/context";
import { Trans } from "app/utils";

import ErrorBoundary from "components/common/ErrorBoundary";
import EventCard from "components/common/EventCard";
import Anchor from "components/display/Anchor";

import EventAttendees from "./view/EventAttendees";
import EventDetails from "./view/EventDetails";
import EventPhotos from "./view/EventPhotos";

interface EnhancedEvent extends CHQ.EventWithRsvp {
  isUserHostOrAdmin: (_profile: CHQ.Profile) => boolean;
}

const shouldShowEventLiveBanner = (event: EnhancedEvent, profile: CHQ.Profile) => (
  (new Date(event.endsAt) > new Date()) && (event.host.id === profile.id)
);

type EventViewProps = {
  event: EnhancedEvent;
  onEventUpdate: (_update: Partial<CHQ.EventWithRsvp>) => void;
  onRefreshInvitations: () => void;
};

const EventView: React.FC<EventViewProps> = ({ event, onEventUpdate, onRefreshInvitations }) => {
  const { profile: rawProfile } = useProfile();

  // This shouldn't be necessary but it is because currently useProfile is lying
  // about its state. In reality it's not a union, it's just a Profile, because
  // if we've gotten this far we're definitely logged in.
  const profile = rawProfile as CHQ.Profile;

  return (
    <>
      <div className="EventView">
        <div>
          <EventCard
            event={event}
            inList={false}
            isDetailedView
            onEventUpdate={onEventUpdate}
            onRefreshInvitations={onRefreshInvitations}
            shouldShowEventLiveBanner={shouldShowEventLiveBanner}
            profile={profile}
          />
        </div>
        <div className="max-width-1120">
          <div className="row">
            <div className="col-sm-5 col-sm-push-7 right-col">
              <EventAttendees
                event={event}
                onEventUpdate={onEventUpdate}
                onRefreshInvitations={onRefreshInvitations}
              />
            </div>
            <div className="col-sm-7 col-sm-pull-5 left-col">
              <div className="left-col__wrapper box-shadow">
                <div className="row">
                  <ul className="nav nav-tabs nav-tabs-green">
                    <li>
                      <NavLink to={`/events/${event.id}`} exact activeClassName="active">
                        <Trans>Details</Trans>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to={`/events/${event.id}/photos`} exact activeClassName="active">
                        <Trans>Photos</Trans>
                      </NavLink>
                    </li>
                    <Anchor to={`/events/${event.id}/photos/create`} primary inverted icon="image-add">
                      <Trans>Add Photos</Trans>
                    </Anchor>
                  </ul>
                </div>
                <div className="row">
                  <ErrorBoundary>
                    <Switch>
                      <Route
                        exact
                        path="/events/:eventId"
                        render={() => <EventDetails event={event} />}
                      />
                      <Route
                        exact
                        path="/events/:eventId/photos"
                        render={() => <EventPhotos event={event} />}
                      />
                    </Switch>
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventView;
